import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { ordersApi } from '../api/client.js';
import { useAuth } from './useAuth.jsx';

/**
 * Order notifications:
 *  - Polls /api/orders/counts every POLL_MS while the user is logged in
 *  - When PENDING count increases, plays a sound, shows a browser notification,
 *    and exposes a `bump` event that pages can react to.
 *  - Persists the last-seen pending count in localStorage so refreshing the page
 *    doesn't re-trigger old notifications.
 *
 * Usage:
 *   <OrderNotificationsProvider>...</OrderNotificationsProvider>
 *   const { pendingCount, hasUnread, markRead, requestPermission } = useOrderNotifications();
 */

const STORAGE_KEY = 'almenu_last_seen_pending';
const PREFS_KEY = 'almenu_notif_prefs';

const DEFAULT_PREFS = {
  soundEnabled: true,
  toastEnabled: true,
  pollIntervalSec: 20, // 0 = disabled, otherwise 10/20/30/60
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

const Ctx = createContext({
  pendingCount: 0,
  hasUnread: false,
  markRead: () => {},
  requestPermission: () => {},
  permissionState: 'default',
  prefs: DEFAULT_PREFS,
  setPrefs: () => {},
});

export function useOrderNotifications() {
  return useContext(Ctx);
}

// Generate a short pleasant chime via Web Audio API — no external file needed.
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    // Two-tone bell: C5 then E5
    const tones = [
      { freq: 523.25, start: 0,   dur: 0.18 },
      { freq: 659.25, start: 0.12, dur: 0.32 },
    ];
    for (const t of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = t.freq;
      gain.gain.setValueAtTime(0.0001, now + t.start);
      gain.gain.exponentialRampToValueAtTime(0.25, now + t.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur + 0.05);
    }
    setTimeout(() => ctx.close().catch(() => {}), 1000);
  } catch {
    // Ignore — audio just won't play
  }
}

function showBrowserNotification(newCount) {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    const title = newCount === 1 ? 'New order received' : `${newCount} new orders received`;
    const n = new Notification(title, {
      body: 'Click to view your orders dashboard.',
      icon: '/favicon.svg',
      tag: 'almenu-new-order',
      renotify: true,
    });
    n.onclick = () => {
      window.focus();
      window.location.href = '/orders';
      n.close();
    };
  } catch {
    // ignore
  }
}

export function OrderNotificationsProvider({ children }) {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [hasUnread, setHasUnread] = useState(false);
  const [permissionState, setPermissionState] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default'
  );
  const [prefs, setPrefsState] = useState(loadPrefs);
  const lastSeenRef = useRef(parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10));
  const initRef = useRef(true);

  const setPrefs = useCallback((updater) => {
    setPrefsState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const markRead = useCallback(() => {
    setHasUnread(false);
    lastSeenRef.current = pendingCount;
    localStorage.setItem(STORAGE_KEY, String(pendingCount));
  }, [pendingCount]);

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      setPermissionState(Notification.permission);
      return Notification.permission;
    }
    const result = await Notification.requestPermission();
    setPermissionState(result);
    return result;
  }, []);

  // Poll while logged in (respects pollIntervalSec; 0 = disabled)
  useEffect(() => {
    if (!user) {
      setPendingCount(0);
      setHasUnread(false);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const counts = await ordersApi.counts();
        if (cancelled) return;
        const pending = counts?.PENDING || 0;
        setPendingCount(pending);

        if (initRef.current) {
          // First poll after login/reload — establish baseline without firing
          initRef.current = false;
          if (pending <= lastSeenRef.current) {
            setHasUnread(false);
          } else {
            setHasUnread(true);
          }
          lastSeenRef.current = pending;
          return;
        }

        if (pending > lastSeenRef.current) {
          const delta = pending - lastSeenRef.current;
          if (prefs.soundEnabled) playChime();
          showBrowserNotification(delta);
          setHasUnread(true);
        }
        lastSeenRef.current = pending;
      } catch {
        // ignore — try next tick
      }
    };

    // Always do an initial fetch so the badge populates
    poll();

    if (!prefs.pollIntervalSec || prefs.pollIntervalSec <= 0) {
      // Polling disabled — only the one-shot fetch above
      return () => { cancelled = true; };
    }

    const id = setInterval(poll, prefs.pollIntervalSec * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, prefs.pollIntervalSec, prefs.soundEnabled]);

  return (
    <Ctx.Provider value={{
      pendingCount, hasUnread, markRead, requestPermission, permissionState,
      prefs, setPrefs,
    }}>
      {children}
    </Ctx.Provider>
  );
}
